import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import {
  formatUrl,
  type FormatUrlParams,
} from 'frontend-utils/utils/url/format-url';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type {
  CustomReference,
  Reference,
} from 'lumen/components/market/total-revenue/reference-picker';
import type { TotalRevenueBreakdownItem } from 'lumen/models';
import type MarketTotalRevenueRoute from 'lumen/routes/market/total-revenue';
import {
  getPropertyIdsQueryParam,
  type MarketTotalRevenueParams,
} from 'lumen/routes/market/total-revenue';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  DATE_AGGREGATION,
  HEADER_HEIGHT,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import {
  METRIC_PAR_TRANSLATIONS,
  METRIC_TRANSLATIONS,
  METRICS,
  VIEWS,
} from 'lumen/utils/constants/total-revenue-constants';
import type { ModelFrom } from 'lumen/utils/routes';

const DATEPICKER_SIDEBAR_IDS = {
  MONTH_TO_DATE: 'month_to_date',
  QUARTER_TO_DATE: 'quarter_to_date',
  YEAR_TO_DATE: 'year_to_date',
  LAST_90_DAYS: 'last_90_days',
  CUSTOM: 'custom',
};

const FILTER_BAR_KEYS = {
  DATE: 'date',
  PROPERTY: 'property',
};

export type ReferencePeriod = Reference['id'];

export default class MarketTotalRevenueController extends Controller {
  declare model: ModelFrom<MarketTotalRevenueRoute>;
  queryParams = [
    'view',
    'fromDate',
    'toDate',
    'referencePeriods',
    'referencePeriodCustomDate',
    'portfolios',
    'properties',
    'dateFilterFrom',
    'dateFilterTo',
    'isPerAvailableRoom',
    'focusedProperties',
    'trendMetric',
    'trendAggregation',
    'localReference',
    'departmentId',
  ];

  @service declare ajax: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];
  @service declare totalRevenue: Services['total-revenue'];
  @service('total-revenue/date')
  declare totalRevenueDate: Services['total-revenue/date'];
  @service('total-revenue/myhotels')
  declare totalRevenueMyhotels: Services['total-revenue/myhotels'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked canShowIncompleteDataBanner = true;
  @tracked dateFilterFrom?: string;
  @tracked dateFilterTo?: string;
  @tracked departmentId?: string;
  @tracked focusedProperties =
    DEFAULT_QP_VALUES.market['total-revenue'].focusedProperties;
  @tracked fromDate?: string;
  @tracked isPerAvailableRoom =
    DEFAULT_QP_VALUES.market['total-revenue'].isPerAvailableRoom;
  @tracked isShowingIncompleteDataModal = false;
  @tracked referencePeriods =
    DEFAULT_QP_VALUES.market['total-revenue'].referencePeriods;
  // eslint-disable-next-line sort-class-members/sort-class-members
  @tracked localReference = this.referencePeriods[0];
  // eslint-disable-next-line sort-class-members/sort-class-members
  @tracked portfolios = DEFAULT_QP_VALUES.market['total-revenue'].portfolios;
  // eslint-disable-next-line sort-class-members/sort-class-members
  @tracked properties = DEFAULT_QP_VALUES.market['total-revenue'].properties;
  /**
   * Date for custom reference period if custom is selected.
   */
  // eslint-disable-next-line sort-class-members/sort-class-members
  @tracked referencePeriodCustomDate?: string;
  /**
   * Comma-separated list of reference periods.
   * e.g. "STLY, custom"
   */
  @tracked toDate?: string;
  @tracked trendAggregation =
    DEFAULT_QP_VALUES.market['total-revenue'].trendAggregation;
  @tracked trendMetric = DEFAULT_QP_VALUES.market['total-revenue'].trendMetric;
  @tracked view = DEFAULT_QP_VALUES.market['total-revenue'].view;

  mainLoadingBarPositionTop = HEADER_HEIGHT + SUB_HEADER_HEIGHT;

  /**
   * Used for the FilterBar component.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get activeFilters() {
    const filters = [];

    if (this.dateFilterFrom && this.dateFilterTo) {
      filters.push({
        key: FILTER_BAR_KEYS.DATE,
        translationKey: 'general.date',
        values: [{ displayValue: this.formattedDateFilter }],
      });
    }

    const focusedProperties = this.possibleProperties.filter((p) =>
      // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
      this.focusedProperties.includes(p.id),
    );

    filters.push({
      key: FILTER_BAR_KEYS.PROPERTY,
      translationKey: 'general.property.plural',
      values: focusedProperties.map((property) => ({
        key: property.id,
        displayValue: property.name,
      })),
    });

    return filters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datepickerSidebarLists() {
    return [
      {
        title: this.intl.t('total_revenue.selected_period'),
        options: [
          {
            id: DATEPICKER_SIDEBAR_IDS.MONTH_TO_DATE,
            text: tKey('total_revenue.month_to_date'),
            range: this.totalRevenueDate.monthToDateRange,
          },
          {
            id: DATEPICKER_SIDEBAR_IDS.QUARTER_TO_DATE,
            text: tKey('total_revenue.quarter_to_date'),
            range: this.totalRevenueDate.quarterToDateRange,
          },
          {
            id: DATEPICKER_SIDEBAR_IDS.YEAR_TO_DATE,
            text: tKey('total_revenue.year_to_date'),
            range: this.totalRevenueDate.yearToDateRange,
          },
          {
            id: DATEPICKER_SIDEBAR_IDS.LAST_90_DAYS,
            text: tKey('total_revenue.last_90_days'),
            range: this.totalRevenueDate.last90DaysRange,
          },
          {
            id: DATEPICKER_SIDEBAR_IDS.CUSTOM,
            text: tKey('total_revenue.custom'),
            range: this.totalRevenueDate.monthToDateRange,
          },
        ],
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateRange() {
    const fromDate = this.totalRevenueDate.getFromDateInLocalTimeWithFallback(
      this.fromDate,
    );
    const toDate = this.totalRevenueDate.getToDateInLocalTimeWithFallback(
      this.toDate,
    );

    return {
      start: fromDate,
      end: toDate,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateRangeNumberOfDays() {
    return this.dateRange.end.diff(this.dateRange.start, 'day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get departmentName() {
    return this.model.totalRevenueDashboard.last?.value?.departmentList.find(
      (item) => item.id === this.departmentId,
    )?.name;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDateFilter() {
    if (!this.dateFilterFrom || !this.dateFilterTo) {
      return '';
    }

    const fromInLocalTime = this.hotelAndMarketService.toLocalTime(
      this.dateFilterFrom,
    );
    const toInLocalTime = this.hotelAndMarketService.toLocalTime(
      this.dateFilterTo,
    );

    return fromInLocalTime.format('ll') === toInLocalTime.format('ll')
      ? fromInLocalTime.format('ll')
      : `${fromInLocalTime.format('ll')} - ${toInLocalTime.format('ll')}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelsWithReservationLink() {
    return this.possibleProperties.some(
      (property) => property.hasTransactionToReservationLink,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDashboardLoading() {
    return (
      this.model.totalRevenueDashboard.isRunning ||
      (this.isStrategyView && this.model.segmentationBreakdowns.isRunning)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDepartmentsView() {
    return this.view === VIEWS.DEPARTMENTS;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSegmentationBreakdownEmptyStateShown() {
    const isSegmentationBreakdownsLoading =
      this.model.segmentationBreakdowns.isRunning;
    const hasMatchedNonRoomRevenue = Boolean(
      this.model.segmentationBreakdowns.last?.value?.summary
        .matchedNonRoomRevenue,
    );

    return !isSegmentationBreakdownsLoading && !hasMatchedNonRoomRevenue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isStrategyView() {
    return this.view === VIEWS.STRATEGY;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get localReferenceOptions() {
    return this.referencePeriods.map((referencePeriod) => {
      return {
        id: referencePeriod,
        label: this.totalRevenue.translateReferenceType(referencePeriod),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfHotels() {
    return this.model.totalRevenueDashboard.last?.value?.portfolio.length;
  }

  /**
   * Used for the Prism::ComboSelectInput portfolio filter `@options` argument
   */
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get portfolioFilterOptions() {
    return this.possiblePortfolios
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((portfolio) => ({ id: portfolio.id, label: portfolio.name }));
  }

  /**
   * Used for the Prism::ComboSelectInput portfolio filter `@value` argument
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get portfolioFilterValue() {
    const isAllPortfoliosSelected = this.portfolios.length === 0;
    return this.portfolioFilterOptions.filter(
      (option) =>
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
        isAllPortfoliosSelected || this.portfolios.includes(option.id),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possiblePortfolios() {
    return this.totalRevenueMyhotels.portfolios;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleProperties() {
    return this.selectedPortfolios.flatMap((portfolio) => [
      ...portfolio.hotels,
    ]);
  }

  /**
   * Used for the Prism::ComboSelectInput property filter `@options` argument
   */
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get propertyFilterOptions() {
    return this.possibleProperties
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((property) => ({
        id: property.id,
        label: property.name,
        disabled:
          this.view === VIEWS.STRATEGY &&
          !property.hasTransactionToReservationLink,
      }));
  }

  /**
   * Used for the Prism::ComboSelectInput property filter `@value` argument
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get propertyFilterValue() {
    const isAllPropertiesSelected = this.properties.length === 0;
    return this.propertyFilterOptions
      .filter((option) => !option.disabled)
      .filter(
        (option) =>
          // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
          isAllPropertiesSelected || this.properties.includes(option.id),
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodPickerMaxDate() {
    return this.dateRange.start.subtract(1, 'day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodPickerReferencePeriods() {
    return this.referencePeriods.map((referencePeriod) => {
      if (referencePeriod === 'custom') {
        return {
          id: referencePeriod,
          fromDate: this.hotelAndMarketService.toLocalTime(
            this.referencePeriodCustomDate,
          ),
        };
      }

      return {
        id: referencePeriod,
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationBreakdownItems() {
    const breakdowns = this.model.segmentationBreakdowns.last?.value;

    if (!breakdowns) {
      return [];
    }

    return [...breakdowns.segment];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedLocalReferenceOption() {
    return this.localReferenceOptions.find(
      (option) => option.id === this.localReference,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPortfolios() {
    const isAllPortfoliosSelected = this.portfolios.length === 0;
    return this.possiblePortfolios.filter(
      (portfolio) =>
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
        isAllPortfoliosSelected || this.portfolios.includes(portfolio.id),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedProperties() {
    const isAllPropertiesSelected = this.properties.length === 0;
    return this.possibleProperties.filter(
      (property) =>
        // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
        isAllPropertiesSelected || this.properties.includes(property.id),
    );
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get trendAggregationOptions() {
    return [
      { id: DATE_AGGREGATION.DAY, label: this.intl.t('general.day') },
      { id: DATE_AGGREGATION.WEEK, label: this.intl.t('general.week') },
      { id: DATE_AGGREGATION.MONTH, label: this.intl.t('general.month') },
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get trendMetricOptions() {
    const translationMappingToUse = this.totalRevenue.isPerAvailableRoom
      ? METRIC_PAR_TRANSLATIONS
      : METRIC_TRANSLATIONS;

    return Object.values(METRICS).map((trendMetric) => ({
      id: trendMetric,
      label: this.intl.t(translationMappingToUse[trendMetric]),
    }));
  }

  @action choosePortfolio(options: typeof this.portfolioFilterOptions): void {
    const selectedPortfolioIds = options.map((option) => option.id);
    // @ts-expect-error: Argument of type '(string | null)[]' is not assignable to parameter of type 'string[]'.
    this.setSelectedPortfolios(selectedPortfolioIds);

    this.usageAnalytics.track('Portfolio Comparison Changed', {
      eventTrigger: 'portfolio-comparison-changed',
    });
  }

  @action chooseProperty(options: typeof this.propertyFilterOptions): void {
    const selectedPropertyIds = options.map((option) => option.id);
    // @ts-expect-error: Argument of type '(string | null)[]' is not assignable to parameter of type 'string[]'.
    this.setSelectedProperties(selectedPropertyIds);

    this.usageAnalytics.track('Property Comparison Changed', {
      eventTrigger: 'property-comparison-changed',
    });
  }

  @action exportSourceTransactions(source: TotalRevenueBreakdownItem): void {
    const marketId = this.hotelAndMarketService.market.id;

    const fromDateInLocalTime =
      this.totalRevenueDate.getFromDateInLocalTimeWithFallback(this.fromDate);
    const toDateInLocalTime =
      this.totalRevenueDate.getToDateInLocalTimeWithFallback(this.toDate);

    // Use focused properties if they exist, otherwise use the portfolio and property filters
    const propertyIds = this.focusedProperties.length
      ? this.focusedProperties
      : getPropertyIdsQueryParam(
          this.possiblePortfolios,
          this.portfolios,
          this.properties,
        );

    const queryParams: FormatUrlParams = {
      // @ts-expect-error:  Type 'string | null' is not assignable to type 'string | number | boolean | string[] | number[]'.
      market_id: marketId,
      from_date: fromDateInLocalTime.format('YYYY-MM-DD'),
      to_date: toDateInLocalTime.format('YYYY-MM-DD'),
      'reference_periods[]': this.referencePeriods,
      // @ts-expect-error: Type '(string | null)[]' is not assignable to type 'string | number | boolean | string[] | number[]'.
      'subscription_ids[]': propertyIds,
      our_category: source.department?.name ?? '',
      our_sub_category: source.name,
      currency: this.totalRevenue.currency,
    };

    if (this.referencePeriodCustomDate) {
      queryParams['custom_from_date'] = this.referencePeriodCustomDate;
    }

    const url = formatUrl(
      '/apigateway/v1/app/download/totalrevenue/transactions',
      queryParams,
      this.ajax.host,
    );

    this.usageAnalytics.track('Transactions Exported', {
      eventTrigger: 'transactions-exported',
      departmentName: source.department?.name,
      sourceName: source.name,
    });

    openUrl(url);
  }

  @action handleFilterBarRemove(categoryKey: string, filterKey: string): void {
    if (categoryKey === FILTER_BAR_KEYS.DATE) {
      this.dateFilterFrom = undefined;
      this.dateFilterTo = undefined;
      return;
    }

    this.removeFocusedProperty(filterKey);
  }

  @action handleFocusProperty(propertyId: string): void {
    if (this.focusedProperties.includes(propertyId)) {
      this.removeFocusedProperty(propertyId);
      return;
    }

    this.focusedProperties = [...this.focusedProperties, propertyId];
    this.showPropertyAddedToast(propertyId);

    this.usageAnalytics.track('Portfolio Hotel Filtered', {
      eventTrigger: 'portfolio-filtered',
    });
  }

  @action handlePerAvailableRoomToggleChange(isChecked: boolean): void {
    this.isPerAvailableRoom = isChecked;

    this.usageAnalytics.track('View Per Available Room Toggled', {
      eventTrigger: 'view-per-available-room',
      isEnabled: isChecked,
    });
  }

  @action onCurrencyChange(): void {
    this.send('refreshRoute');
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  portfolioBuildLabelWithCount(count: number) {
    return this.intl.t('total_revenue.portfolio.count', { count });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  propertyBuildLabelWithCount(count: number) {
    return this.intl.t('total_revenue.property.count', { count });
  }

  @action removeFocusedProperty(propertyId: string): void {
    this.focusedProperties = this.focusedProperties.filter(
      (id) => id !== propertyId,
    );
  }

  @action removePropertiesFromFilter(propertyIds: string[]): void {
    const selectedPropertyIds = this.selectedProperties.map((p) => p.id);
    this.setSelectedProperties(
      // @ts-expect-error: Argument of type '(string | null)[]' is not assignable to parameter of type 'string[]'.
      selectedPropertyIds.filter((id) => !propertyIds.includes(id)),
    );
  }

  @action setSelectedPortfolios(value: string[]): void {
    // If all portfolios are selected, use an empty array to represent that
    const allPortfoliosSelected =
      value.length === this.possiblePortfolios.length;
    this.portfolios = allPortfoliosSelected ? [] : value;

    // Remove properties that are not in the selected portfolios
    const possiblePropertyIds = this.possibleProperties.map((p) => p.id);
    this.setSelectedProperties(
      this.properties.filter((p) => possiblePropertyIds.includes(p)),
    );
  }

  @action setSelectedProperties(value: string[]): void {
    // If all properties are selected, use an empty array to represent that
    const allPropertiesSelected =
      value.length === this.possibleProperties.length;
    this.properties = allPropertiesSelected ? [] : value;

    // Remove focused properties that are not in the selected properties
    this.focusedProperties = this.focusedProperties.filter((id) =>
      value.includes(id),
    );

    // Allow incomplete data banner to be shown again
    this.canShowIncompleteDataBanner = true;
  }

  @action showFilterAddedToast(filter: string): void {
    const message = this.intl.t('total_revenue.toast.added_as_page_filter', {
      filter,
    });

    this.toast.success(message, { showIcon: true });
  }

  @action showPropertyAddedToast(propertyId: string): void {
    const property = this.possibleProperties.find((p) => p.id === propertyId);

    if (property) {
      this.showFilterAddedToast(property.name);
    }
  }

  @action updateBreakdownLocalReference(
    localReference: (typeof this.localReferenceOptions)[0],
  ): void {
    this.localReference = localReference.id;

    this.usageAnalytics.track('Period Comparison Changed', {
      eventTrigger: 'breakdown-comparison-change',
      filterAppliedType: 'Breakdown',
      periodName: localReference.id,
    });
  }

  @action updateDateFilter(from: Dayjs, to: Dayjs): void {
    this.dateFilterFrom = from.format('YYYY-MM-DD');
    this.dateFilterTo = to.format('YYYY-MM-DD');

    this.showFilterAddedToast(this.formattedDateFilter);

    this.usageAnalytics.track('Trend Analysis Date Filtered', {
      eventTrigger: 'trend-analysis-date-filter',
      amountOfDays: to.diff(from, 'days') + 1,
      startDaysOffsetFromToday: from.diff(dayjs(), 'days'),
    });
  }

  @action updateDateRange(dateRange: {
    end: Dayjs | null;
    start: Dayjs;
  }): void {
    const formattedFromDate = dateRange.start.format('YYYY-MM-DD');
    const formattedToDate =
      dateRange.end?.format('YYYY-MM-DD') ?? formattedFromDate;

    const queryParams: Partial<MarketTotalRevenueParams> = {
      fromDate: formattedFromDate,
      toDate: formattedToDate,
    };

    // Switch to monthly if the new range is over 92 days (+- 3 months or a quarter)
    const numberOfDays = dateRange.end?.diff(dateRange.start, 'days') ?? 1;
    queryParams.trendAggregation = numberOfDays > 92 ? 'month' : 'day';

    this.router.transitionTo({ queryParams });

    // Allow incomplete data banner to be shown again
    this.canShowIncompleteDataBanner = true;
  }

  @action updateReferencePeriods(referencePeriods: Reference[]): void {
    const customReferencePeriod = referencePeriods.find(
      (referencePeriod): referencePeriod is CustomReference =>
        referencePeriod.id === 'custom',
    );

    const queryParams = {
      referencePeriods: referencePeriods.map(
        (referencePeriod) => referencePeriod.id,
      ),
      referencePeriodCustomDate:
        customReferencePeriod?.fromDate.format('YYYY-MM-DD'),
    };

    this.router.transitionTo({ queryParams });

    // Allow incomplete data banner to be shown again
    this.canShowIncompleteDataBanner = true;
  }

  @action updateSelectedDepartmentId(departmentId: string | undefined): void {
    this.departmentId = departmentId;

    if (departmentId) {
      this.usageAnalytics.track('Breakdown Sources Investigated', {
        eventTrigger: 'breakdown-source',
        departmentName: this.departmentName,
      });
    }
  }

  @action updateStrategyLocalReference(
    localReference: (typeof this.localReferenceOptions)[0],
  ): void {
    this.localReference = localReference.id;

    this.usageAnalytics.track('Period Comparison Changed', {
      eventTrigger: 'strategy-comparison-change',
      filterAppliedType: 'Production by type',
      periodName: localReference.id,
    });
  }

  @action updateTrendAggregation(
    trendAggregation: (typeof this.trendAggregationOptions)[0],
  ): void {
    this.trendAggregation = trendAggregation.id;

    this.usageAnalytics.track('Trend Analysis Aggregation Changed', {
      eventTrigger: 'trend-analysis-aggregation-change',
      aggregation: trendAggregation.id,
    });
  }

  @action updateTrendMetric(
    trendMetric: (typeof this.trendMetricOptions)[0],
  ): void {
    this.trendMetric = trendMetric.id;

    this.usageAnalytics.track('Trend Analysis KPI Changed', {
      eventTrigger: 'trend-analysis-by-kpi-change',
      kpiName: trendMetric.id,
    });
  }

  @action updateView(view: string): void {
    if (view === VIEWS.DEPARTMENTS || view === VIEWS.STRATEGY) {
      this.view = view;

      this.usageAnalytics.track('View Changed', {
        eventTrigger: 'view-changed',
        viewName: view,
      });
    }
  }
}
